<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import type { Ref } from 'vue';
import { computed, inject, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useDark, useElementHover, useEventListener } from '@vueuse/core';
import { useUserStore } from '@/config/store/userStore';
import { useApplicationStore } from '@/config/store/applicationStore';
import { getDrawerMenu, getDrawerMenuSuperAdmin, getDrawerMenuAdmin } from '@/services/application/constantes';
import { APP_VERSION } from '@/config/constantes';
import { resourceApi, messageApi, elearningAssignmentApi } from '@/services/api';
import { isModuleActive } from '@/services/application';
import { guardExport } from '@/config/router/routes';
import { canUseAiAssistant } from '@/config/router/navigationsGuards/guardAiAssistant';
import { canUseAcademy } from '@/config/router/navigationsGuards/guardAcademy';
import { useAcademySummary } from '@/composables/academy/useAcademySummary';
import { useChatStore } from '@/config/store/chatStore';
import { storeToRefs } from 'pinia';

// Import child components
import DrawerHeader from './DrawerHeader.vue';
import DrawerMenuList from './DrawerMenuList.vue';
import DrawerFooter from './DrawerFooter.vue';

// Keep necessary imports for this component
import SearchCommand from '@/components/search/SearchCommand.vue';
import BButton from '@/components/common/bButton.vue';
import Loading from '@/components/common/loading.vue'; // Might be needed if updateProfile has visual cue here
import SolarCloudDownloadOutline from '~icons/solar/cloud-download-outline';
import MaterialSymbolsAdminPanelSettingsOutlineSharp from '~icons/material-symbols/admin-panel-settings-outline-sharp';
import IcRoundQuestionMark from '~icons/ic/round-question-mark';
import SolarSquareAcademicCap2LineDuotone from '~icons/solar/square-academic-cap-2-line-duotone';
import SolarDatabaseLineDuotone from '~icons/solar/database-line-duotone';
import PhSparkle from '~icons/ph/sparkle';
import MdiExitRun from '~icons/mdi/exit-run'; // Import logout icon

// ----- Core Logic retained from original -----
const emit = defineEmits<{
  'change-password': []
}>();

const { t } = useI18n();
const route = useRoute();
const userStore = useUserStore();
const applicationStore = useApplicationStore();
const isDark = useDark(); // Keep for top-level theme logic if needed, or remove if only in footer

// State
const drawerRef = ref<HTMLElement | null>(null);
const isHovering = useElementHover(drawerRef);
// Keyboard focus opens the drawer like the mouse does: the CSS reads it from the drawer-keyboard-focus class.
const hasKeyboardFocus = ref(false);
useEventListener(drawerRef, 'focusin', (event: FocusEvent) => {
  hasKeyboardFocus.value = (event.target as HTMLElement).matches(':focus-visible');
});
useEventListener(drawerRef, 'focusout', (event: FocusEvent) => {
  if (!drawerRef.value?.contains(event.relatedTarget as Node | null)) hasKeyboardFocus.value = false;
});
const isExpanded = computed(() => isHovering.value || hasKeyboardFocus.value);
const isOpen = ref(false); // Mobile open state
const isUpdatingProfile = ref(false); // State for profile update loading
const imageError = ref(false); // Kept separate from header for now, could be prop if needed there
const isDropDownOpen = ref(false); // State for the header dropdown

// Inbox access and unread count
const isAuthenticated = computed(() => !!(userStore.token && userStore.user?._id))

const canAccessInbox = computed(() => {
  if (!isAuthenticated.value || !userStore.user?.firm) return false
  const hasRecruitmentAccess = isModuleActive(userStore.user.firm, 4) && userStore.user.profile?.modules?.recruitment?.isAccess
  return hasRecruitmentAccess
})

const chatStore = useChatStore()
const { canAccess: canAccessChat, unreadCount: chatUnreadCount } = storeToRefs(chatStore)

const inboxUnreadCount = ref(0)
let inboxUnreadCountInterval: ReturnType<typeof setInterval> | null = null

const fetchInboxUnreadCount = async () => {
  if (!isAuthenticated.value || !canAccessInbox.value) return
  try {
    const response = await messageApi.getUnreadCount().promise
    inboxUnreadCount.value = response.unreadCount
  } catch (err) {
    console.error('Error fetching inbox unread count:', err)
  }
}

const stopInboxPolling = () => {
  if (inboxUnreadCountInterval) {
    clearInterval(inboxUnreadCountInterval)
    inboxUnreadCountInterval = null
  }
}

watch(
  [isAuthenticated, canAccessInbox],
  ([connected, canAccess]) => {
    stopInboxPolling()
    if (!connected || !canAccess) return
    fetchInboxUnreadCount()
    inboxUnreadCountInterval = setInterval(fetchInboxUnreadCount, 600000) // Poll every 10 minutes
  },
  { immediate: true },
)

onUnmounted(() => {
  stopInboxPolling()
})

// "My trainings" badge: e-learning modules assigned and not started yet. Fetched again when leaving the
// training pages where they get started, silent on failure.
const canFollowElearning = computed(() => {
  const training = userStore.user?.profile?.modules?.training
  return isAuthenticated.value && !!(training?.elearningCatalogAccess || training?.myTrainingsAccess)
})
const elearningTodoCount = ref(0)

const fetchElearningTodoCount = async () => {
  if (!canFollowElearning.value) {
    elearningTodoCount.value = 0
    return
  }
  try {
    elearningTodoCount.value = (await elearningAssignmentApi.todoCount().promise)?.count || 0
  } catch {
    // The badge is a hint: never an error for it.
  }
}

watch(() => (canFollowElearning.value ? userStore.user?._id : null), fetchElearningTodoCount, { immediate: true })
watch(() => route.name, (name, previous) => {
  if (['MyTraining', 'Elearning', 'ElearningAssignmentPlayer'].includes(String(previous)))
    fetchElearningTodoCount()
})

function withMyTrainingBadge(items) {
  if (!elearningTodoCount.value) return items
  return items.map(item => item.subPages?.some(page => page.route === 'MyTraining')
    ? { ...item, subPages: item.subPages.map(page => page.route === 'MyTraining' ? { ...page, badge: elearningTodoCount.value } : page) }
    : item)
}

// Academy badge: lessons new since the last visit + lessons to see again. Fetched once, silent on failure.
const { summary: academySummary, badgeCount: academyBadgeCount, loadOnce: loadAcademySummary } = useAcademySummary()
const canAccessAcademy = computed(() => isAuthenticated.value && canUseAcademy(userStore.user))

watch(
  () => (canAccessAcademy.value ? userStore.user?._id : null),
  (userId) => { loadAcademySummary(userId) },
  { immediate: true },
)

// Scroll lock injection
const scrollBlocked = inject<Ref<boolean>>('scrollBlocked');
watch(isOpen, (newValue) => {
  if (scrollBlocked) {
    scrollBlocked.value = newValue;
  }
});

// Computed Properties
const isSuperAdminRoute = computed(() => {
  return !!route.matched.find(currentRoute => currentRoute.meta.moduleId === 'SUPER_ADMIN');
});

const isAdminUser = computed(() => {
  return userStore.user?.role === 'admin' && !userStore.user?._firmId;
});

const isManagementRoute = computed(() => {
  return !!route.matched.find(currentRoute => currentRoute.meta.requiresManagement);
});

const drawerColor = computed(() => {
  return isSuperAdminRoute.value ? 'bg-base' : 'bg-base'; // Adjust colors if needed
});

const availableProfiles = computed(() => {
    if (!userStore.user) return [];
    // Ensure current profile is not duplicated if already in availableProfiles
    const currentProf = userStore.user.profile;

    const otherProfiles = userStore.user.availableProfiles?.filter(p => p._id !== currentProf?._id && !p.isCandidateProfile) || [];
    if (currentProf?.isCandidateProfile) return otherProfiles;
    return currentProf ? [...otherProfiles, currentProf] : otherProfiles;
});

// Use the store state directly for fixed status
const isFixed = computed(() => applicationStore.drawer.fixed);

// Menu Items Construction (kept inside)
const menuItems = computed(() => {
    // Admin users (role: 'admin' with no firmId) get the admin menu only
    // They don't have a firm or profile, so we must return early before calling guards
    if (isAdminUser.value) {
        const adminItems = getDrawerMenuAdmin();
        // Add logout at the end
        return [
            ...adminItems,
            { separator: true },
            {
                display: 'common.drawer.logOut', route: 'Disconnection',
                prependIcon: MdiExitRun, moduleId: 'logout',
                color: 'hover:border-red-800! hover:bg-red-800/25!',
            }
        ];
    }

    // If user doesn't have a firm (shouldn't happen for non-admin users, but safety check)
    if (!userStore.user?.firm) {
        return [{
            display: 'common.drawer.logOut', route: 'Disconnection',
            prependIcon: MdiExitRun, moduleId: 'logout',
            color: 'hover:border-red-800! hover:bg-red-800/25!',
        }];
    }

    const baseItems = isSuperAdminRoute.value
        ? getDrawerMenuSuperAdmin()
        : withMyTrainingBadge(getDrawerMenu(userStore.user));

    const specialItems = [];

    if (!isSuperAdminRoute.value && userStore.user) { // Check user exists
        let needsSeparator = false;
        if (guardExport() === true) {
            specialItems.push({
                display: 'common.drawer.export', route: 'Export',
                prependIcon: SolarDatabaseLineDuotone, moduleId: 'export',
                color: 'hover:border-primary-800! hover:bg-primary-800/25!',
            });
            needsSeparator = true;
        }
        if (canUseAiAssistant()) {
            specialItems.push({
                display: 'common.drawer.askAi', route: 'AskAi',
                prependIcon: PhSparkle, moduleId: 'ask-ai',
                color: 'hover:border-primary-800! hover:bg-primary-800/25!',
            });
            needsSeparator = true;
        }
        if (userStore.user.profile?.isSubAdminProfile) {
             specialItems.push({
                display: 'common.drawer.superAdmin', route: 'SuperAdminUsers',
                prependIcon: MaterialSymbolsAdminPanelSettingsOutlineSharp, moduleId: 'sub-admin',
                color: 'hover:border-warning-800! hover:bg-warning-800/25!',
             });
             needsSeparator = true;
        }

        if (needsSeparator && specialItems.length > 0) {
             specialItems.unshift({ separator: true });
        }

        // Add Academy, Documentation and Logout after a potential second separator
         specialItems.push({ separator: true }); // Separator before docs/logout
         // Shown once a lesson is published: an empty academy is no reason to send people there.
         if (canAccessAcademy.value && (academySummary.value?.total || 0) > 0) {
             specialItems.push({
                 display: 'common.drawer.academy', route: 'AcademyHome',
                 prependIcon: SolarSquareAcademicCap2LineDuotone, moduleId: 'academy',
                 color: 'hover:border-primary-800! hover:bg-primary-800/25!',
                 badge: academyBadgeCount.value,
             });
         }
         specialItems.push({
             display: 'common.drawer.documentation',
              link: 'https://bc.direct/fr/docs/new-version',
             prependIcon: IcRoundQuestionMark, moduleId: 'docs',
             color: 'hover:border-primary-800! hover:bg-primary-800/25!',
         });
         specialItems.push({
            display: 'common.drawer.logOut', route: 'Disconnection', // Use route directly
            prependIcon: MdiExitRun, moduleId: 'logout',
            color: 'hover:border-red-800! hover:bg-red-800/25!',
         });
    }
    return [...baseItems, ...specialItems];
});

// --- Methods ---
const toggleMenuMobile = (forceState?: boolean) => {
  isOpen.value = typeof forceState === 'boolean' ? forceState : !isOpen.value;
};

const closeMobileMenu = () => {
    toggleMenuMobile(false);
}

const updateAccessProfile = async (profile) => {
  if (!userStore.user) return;
  isUpdatingProfile.value = true;
  try {

    const currentProfile = userStore.user.profile;

    const newAvailableProfiles = [
      ...userStore.user.availableProfiles.filter(p => p._id !== profile._id),
      currentProfile
    ];
    
    await resourceApi.updateResource(userStore.user._id, {
      accessProfile: profile._id,
      availableProfileIds: newAvailableProfiles.map(p => p._id),
    }).promise;
    // Update store
     userStore.switchUserProfile(profile, newAvailableProfiles); // Assumes store handles internal updates
  } catch (error) {
    console.error("Failed to update profile:", error);
    // Maybe use applicationStore.addError(...)
  } finally {
    isUpdatingProfile.value = false;
  }
};

const toggleFixedState = () => {
  applicationStore.drawer.fixed = !applicationStore.drawer.fixed;
};

const emitChangePassword = () => {
    emit('change-password');
}

</script>

<template>
  <div
    v-if="userStore.user"
    ref="drawerRef"
    data-cy="navigation-drawer"
    class="relative md:mx-0 md:mt-0 md:bottom-0 md:left-0 md:top-0 md:fixed transition-all duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none z-50 flex flex-col md:grow card-base p-0 h-16 md:h-auto overflow-hidden shrink-0 rounded-t-none rounded-b-xl md:rounded-l-none w-full"
    :class="[
        drawerColor,
        isFixed ? 'md:w-64 drawer-container-fixed' : 'md:w-14 drawer-container shadow-sm',
        { 'drawer-keyboard-focus': hasKeyboardFocus },
        { 'on-mobile-disable-rounded-bottom h-dvh overflow-y-auto': isOpen },
    ]"
  >

    <!-- Header Section -->
    <DrawerHeader
      :user="userStore.user"
      :available-profiles="availableProfiles"
      :current-profile="userStore.user.profile"
      :is-updating-profile="isUpdatingProfile"
      :is-fixed="isFixed"
      :is-open="isOpen"
      :is-hovering="isExpanded && !isFixed"
      :drawer-ref="drawerRef"
      :update-profile-fn="updateAccessProfile"
      :toggle-mobile-menu-fn="toggleMenuMobile"
      :toggle-fixed-fn="toggleFixedState"
      :emit-change-password-fn="emitChangePassword"
      :can-access-inbox="canAccessInbox"
      :inbox-unread-count="inboxUnreadCount"
      :can-access-chat="canAccessChat"
      :chat-unread-count="chatUnreadCount"
    />


    <!-- Search Command -->
     
    <!-- <SearchCommand
        v-if="userStore.user"
        class="mx-2 shrink-0 search-command"
        :class="{ 'search-command-hidden': !isFixed && !isHovering && !isOpen }"
     /> -->

    <!-- Menu List Section -->
    <!-- Conditional rendering/styling for mobile menu panel -->
     <div
        class="transition-all duration-200 mini-scroll md:relative grow h-auto overflow-y-auto"
        :class="[
           drawerColor,
            // isOpen
            //     ? 'h-[calc(100%-180px)] fixed bottom-2 left-2 right-2 rounded-b-xl overflow-y-auto z-10' // Mobile open style: Take remaining height, fixed position
            //     : '' // Desktop/Mobile closed: Normal flow or hide
        ]"
     >
       <DrawerMenuList
         :menu-items="menuItems"
         :is-super-admin-route="isSuperAdminRoute"
         :close-mobile-menu-fn="closeMobileMenu"
       />
     </div>


    <!-- Footer Section -->
    <DrawerFooter
      :app-version="APP_VERSION"
      :user="userStore.user"
      :is-fixed="isFixed"
      :is-hovering="isExpanded && !isFixed"
      :toggle-fixed-fn="toggleFixedState"
    />
  </div>
</template>

<style scoped>
/* Base classes for fixed/hover state */
.drawer-container { /* Base styles for collapsed state */ }
.drawer-container-fixed { /* Styles when drawer is fixed */ }

/* Plain :hover, not Tailwind's hover: (v4 gates it behind @media (hover: hover), false on touch-screen PCs).
   Keyboard focus opens it too: :focus-visible, since a mouse click also focuses the link. Through the
   .drawer-keyboard-focus class, not :has(:focus-visible): with it Chrome restyled the whole page each time an
   element was added or removed anywhere (most of the time a CV preview took over a long screening list). */
@media (min-width: 768px) {
  .drawer-container:is(:hover, .drawer-keyboard-focus) { width: calc(var(--spacing) * 64); }
}

/* Update Button Text visibility */
.update-button-text { display: none; }
.drawer-container:is(:hover, .drawer-keyboard-focus) .update-button-text,
.drawer-container-fixed .update-button-text {
  display: inline; animation: fade-in 0.5s forwards;
}
@keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }

/* Search Command visibility */
.search-command { transition: opacity 0.3s, max-width 0.3s; max-width: 500px; opacity: 1; }
.search-command-hidden {
    @media (min-width: 768px) { /* Only hide on desktop when collapsed/not hovered */
        opacity: 0;
        max-width: 0;
        overflow: hidden;
        padding-left: 0;
        padding-right: 0;
        margin-left: 0;
        margin-right: 0;
    }
}
.drawer-container:is(:hover, .drawer-keyboard-focus) .search-command-hidden,
.drawer-container-fixed .search-command-hidden {
    @media (min-width: 768px) {
        opacity: 1;
        max-width: 500px;
        /* Re-apply padding/margin if needed */
    }
}


/* Mobile rounded corners */
.on-mobile-disable-rounded-bottom {
  @media screen and (max-width: 768px) {
    border-bottom-right-radius: 0px;
    border-bottom-left-radius: 0px;
  }
}

/* Mini-scroll styles */
.mini-scroll { scrollbar-width: thin; scrollbar-color: var(--color-gray-400) var(--color-gray-200); }
.mini-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
.mini-scroll::-webkit-scrollbar-track { background-color: var(--color-gray-200); border-radius: 3px; }
.mini-scroll::-webkit-scrollbar-thumb { background-color: var(--color-gray-400); border-radius: 3px; }
html.dark .mini-scroll { scrollbar-color: var(--color-gray-600) var(--color-gray-800); }
html.dark .mini-scroll::-webkit-scrollbar-track { background-color: var(--color-gray-800); }
html.dark .mini-scroll::-webkit-scrollbar-thumb { background-color: var(--color-gray-600); }
</style>
